import { Box, Skeleton, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEnvelope, faPhone } from "@fortawesome/pro-solid-svg-icons"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { fetchKitchenDetails, UserWeekMenu } from "../../weekMenuApi"
import { KitchenDetails, KitchenDetailsOpeningTime } from "../../weekMenuTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectKitchen } from "../../../../nonAdminUserSelectors"
import { useExternalLinks } from "../../../../../../shared/hooks/useAppLinks"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { DateTimeFactory, useDateTime } from "../../../../../localization/useDateTime"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"

const useStyles = makeStyles()((theme) => ({
    container: {
        color: theme.palette.common.black,
        backgroundColor: tailwindPalette.blue[200],
        borderRadius: "1rem",
        padding: "1rem",
        marginBottom: "1.875rem",
    },
    section: {
        display: "flex",
        flexDirection: "column",
    },
    leftSection: {
        flex: "65 1 0",
        padding: ".5rem 1rem .5rem .5rem",
    },
    rightSection: {
        flex: "35 1 0",
        maxWidth: "12.5rem",
        aspectRatio: "6 / 5",
        justifyContent: "center",
        alignItems: "center",
    },
    timeSection: {
        borderRadius: "0.625rem",
        padding: "1.25rem",
        backgroundColor: "#FFFFFF40",
        color: tailwindPalette.blue[900],
    },
    contentArea: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1.25rem",
    },
    contactRow: {
        display: "flex",
        alignItems: "center",
    },
    contactRowClickable: {
        cursor: "pointer",
    },
    iconWrap: {
        width: "1.125rem",
        height: "1.125rem",
        marginRight: ".625rem",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
    },
    contactIcon: {
        fontSize: "1.125rem",
        color: theme.palette.tailwind.slate[800],
    },
    schedule: {
        fontSize: "3.75rem",
        fontWeight: 500,
        color: tailwindPalette.blue[900],
        textAlign: "center",
        lineHeight: 1,
    },
    skeletonCard: {
        height: "9.2rem",
        borderRadius: "0.625rem",
        width: "100%",
    },
    skeletonLine: {
        height: "1.3rem",
        borderRadius: "0.625rem",
        width: "80%",
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
}))

type SafeCanteenInfoProps = Readonly<{
    kitchenId?: number
}>

export function SafeCanteenInfo({ kitchenId }: SafeCanteenInfoProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const { data, isLoading } = useApiQuery(fetchKitchenDetails, {
        queryName: UserWeekMenu.KITCHEN_DETAILS,
        dependencies: {
            kitchenId: kitchenId ?? 0,
        },
        enabled: Boolean(kitchenId),
        useErrorBoundary: true,
        select: transformKitchenDetails,
        refetchInterval: RefetchIntervals.medium,
    })

    const handleExternalLink = useExternalLinks()
    const groupedHours = groupOpeningHours(dateTimeFactory, data?.contactDetails.openingTimes)

    return (
        <>
            <Box className={cx(classes.section, classes.leftSection)}>
                <LocalizedStrict id="user-home-canteen-contact-canteen">
                    <Typography variant="h5" fontWeight={500} color="text.primary">
                        Contact canteen
                    </Typography>
                </LocalizedStrict>

                {isLoading && <Skeleton variant="rectangular" className={classes.skeletonLine} />}

                <ErrorBoundary
                    fallback={
                        <UserDashboardErrorFallbackCard
                            classNames={classes.errorCard}
                            contentClassNames={classes.errorContent}
                        />
                    }
                >
                    <Box className={classes.contentArea}>
                        {data?.contactDetails.phoneNumber && (
                            <Box className={classes.contactRow}>
                                <Box className={classes.iconWrap}>
                                    <FontAwesomeIcon icon={faPhone} className={classes.contactIcon} />
                                </Box>
                                <Typography variant="body1" color="text.primary">
                                    {data?.contactDetails.phoneNumber}
                                </Typography>
                            </Box>
                        )}
                        {data?.contactDetails.email && (
                            <Box
                                className={cx(classes.contactRow, classes.contactRowClickable)}
                                onClick={() => handleExternalLink(`mailto:${data?.contactDetails.email}`)}
                            >
                                <Box className={classes.iconWrap}>
                                    <FontAwesomeIcon icon={faEnvelope} className={classes.contactIcon} />
                                </Box>
                                <Typography variant="body1" color="text.primary">
                                    {data?.contactDetails.email}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </ErrorBoundary>
            </Box>

            {isLoading ? (
                <Box className={cx(classes.section, classes.rightSection)}>
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                </Box>
            ) : (
                <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                    <Box className={cx(classes.section, classes.rightSection, classes.timeSection)}>
                        <Stack spacing="0.5rem">
                            {groupedHours.map((group, index) => (
                                <Box key={index}>
                                    <Typography className={classes.schedule}>{group.openingTime}</Typography>
                                    <Typography
                                        fontSize="1rem"
                                        fontWeight={600}
                                        color="text.secondary"
                                        textAlign="center"
                                    >
                                        {group.days.length > 1
                                            ? `${group.days[0]}-${group.days[group.days.length - 1]}`
                                            : group.days[0]}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </ErrorBoundary>
            )}
        </>
    )
}

export function CanteenInfo() {
    const { classes } = useStyles()
    const selectedKitchen = useAppSelector(selectKitchen)

    if (!selectedKitchen) {
        return null
    }

    return (
        <Stack direction={{ xs: "column", md: "row" }} className={classes.container} spacing={0}>
            <QueryErrorBoundary Fallback={CanteenFallback}>
                <SafeCanteenInfo kitchenId={selectedKitchen?.id} />
            </QueryErrorBoundary>
        </Stack>
    )
}

const CanteenFallback = () => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Box marginBottom="1.875rem">
            <UserDashboardErrorFallbackCard
                classNames={classes.errorCard}
                contentClassNames={classes.errorContent}
                errorMessage={l10n.getString("user-dashboard-error-merchant-contact-unavailable")}
            >
                <LocalizedStrict id="user-home-canteen-contact-canteen">
                    <Typography fontWeight={500} variant="h5">
                        Contact canteen
                    </Typography>
                </LocalizedStrict>
            </UserDashboardErrorFallbackCard>
        </Box>
    )
}

const transformKitchenDetails = (data: KitchenDetails) => data

const groupOpeningHours = (dateTimeFactory: DateTimeFactory, data?: KitchenDetailsOpeningTime[]) => {
    if (!data || data.length === 0) return []

    const sortedData = [...data].sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    const grouped: { days: string[]; openingTime: string }[] = []
    let currentGroup: { days: string[]; openingTime: string } | null = null

    sortedData.forEach(({ dayOfWeek, openingTime, closingTime }) => {
        const dayName = dateTimeFactory.now().set({ weekday: dayOfWeek }).toFormat("cccc")
        const openHour = dateTimeFactory.fromHourMinute(openingTime).hour
        const closeHour = dateTimeFactory.fromHourMinute(closingTime).hour
        const formattedTime = `${openHour}-${closeHour}`

        if (!currentGroup || currentGroup.openingTime !== formattedTime) {
            currentGroup = { days: [dayName], openingTime: formattedTime }
            grouped.push(currentGroup)
        } else {
            currentGroup.days.push(dayName)
        }
    })

    return grouped
}
